import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  GraduationCap,
  Users,
  BarChart3,
  ShieldCheck,
  Award,
  ClipboardList,
  Boxes,
  Settings,
} from "lucide-react";
import { clsx } from "clsx";
import { useAuth } from "@/lib/auth";
import { useBranding, DEFAULT_BRANDING } from "@/lib/branding";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles?: Array<"admin" | "instructor" | "student">;
}

const navItems: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/courses", label: "Courses", icon: BookOpen },
  { to: "/my-learning", label: "My Learning", icon: GraduationCap, roles: ["student"] },
  { to: "/certificates", label: "Certificates", icon: Award, roles: ["student"] },
  { to: "/categories", label: "Categories", icon: Boxes, roles: ["admin"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ["admin", "instructor"] },
  { to: "/audit-logs", label: "Audit Logs", icon: ShieldCheck, roles: ["admin"] },
  { to: "/notifications", label: "Notifications", icon: ClipboardList },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

export function Sidebar() {
  const { user } = useAuth();
  const branding = useBranding();
  const appName = branding?.app_name || DEFAULT_BRANDING.app_name;

  return (
    <aside className="hidden w-64 shrink-0 border-r border-slate-200 bg-white md:flex md:flex-col">
      <div className="flex h-16 items-center gap-2 border-b border-slate-100 px-6">
        {branding?.logo_url ? (
          <img src={branding.logo_url} alt={appName} className="h-8 w-8 shrink-0 rounded-lg object-contain" />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-sm font-bold text-white">
            {appName.trim()[0]?.toUpperCase() ?? "W"}
          </div>
        )}
        <span className="truncate text-lg font-semibold text-slate-900">{appName}</span>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {navItems
          .filter((item) => !item.roles || (user && item.roles.includes(user.role)))
          .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                clsx(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-100",
                )
              }
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          ))}
      </nav>
    </aside>
  );
}
